import React, { useCallback, useEffect, useState } from 'react';
import { FieldType } from './field.types';
import { Checkbox, FormControl, FormControlLabel, FormHelperText, FormLabel, Link, RadioGroup } from '@mui/material';
import { EventRegistrationFormFieldOptions } from '../generated/api/dsv-public/model';
import { useTranslation } from 'react-i18next';
import { useFormValidation } from '../context/FormValidation';
import { useSingleForm } from '../context/SingleForm';

type TermsAndConditionsFieldProps = FieldType & {
  options?: EventRegistrationFormFieldOptions;
};

export const TermsAndConditionsField: React.FC<TermsAndConditionsFieldProps> = ({
  fieldKey,
  name,
  description,
  defaultValue,
  required,
  options,
  onChangeFieldValue,
}) => {
  const [checked, setChecked] = useState<boolean>(Boolean(defaultValue));
  const { t } = useTranslation();
  const { showError, updateFieldValidation } = useFormValidation();
  const { formIndex } = useSingleForm();

  useEffect(() => {
    const isInitialValid = !required || Boolean(defaultValue);
    updateFieldValidation(`${formIndex}-${fieldKey}`, isInitialValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = useCallback(() => {
    setChecked(prev => !prev);
    if (required) {
      updateFieldValidation(`${formIndex}-${fieldKey}`, !checked);
    }
    onChangeFieldValue(fieldKey, !checked ? options?.url : undefined);
  }, [updateFieldValidation, onChangeFieldValue, required, formIndex, fieldKey, checked, setChecked, options]);

  return (
    <FormControl error={showError && required && !checked}>
      <FormLabel id={`${formIndex}-${fieldKey}`} className={required ? 'label-required' : ''}>
        {name}
      </FormLabel>
      <RadioGroup name={fieldKey} aria-labelledby={fieldKey}>
        <FormControlLabel
          value="true"
          control={<Checkbox required={required} onChange={onChange} checked={checked} />}
          label={
            <>
              {options && options['checkbox-label'] ? (
                options['checkbox-label'].split(/({{titleAndUrl}})/g).map((text, idx) => {
                  if (text === '{{titleAndUrl}}') {
                    return (
                      <Link key={idx} href={options?.url} target="_blank">
                        {name}
                      </Link>
                    );
                  } else {
                    return <React.Fragment key={idx}>{text}</React.Fragment>;
                  }
                })
              ) : (
                <>
                  {t('field.terms-and-conditions.accept-the')}
                  <Link href={options?.url} target="_blank">
                    {name}
                  </Link>
                </>
              )}
            </>
          }
        />
      </RadioGroup>
      <FormHelperText>{description}</FormHelperText>
    </FormControl>
  );
};
